import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import { Textarea } from "@mui/joy";
import Box from "@mui/joy/Box";
import Checkbox from "@mui/joy/Checkbox";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import IconButton from "@mui/joy/IconButton";
import Input from "@mui/joy/Input";
import Link from "@mui/joy/Link";
import Option from "@mui/joy/Option";
import Select, { selectClasses } from "@mui/joy/Select";
import Sheet from "@mui/joy/Sheet";
import Tab, { tabClasses } from "@mui/joy/Tab";
import TabList from "@mui/joy/TabList";
import Table from "@mui/joy/Table";
import Tabs from "@mui/joy/Tabs";
import Tooltip from "@mui/joy/Tooltip";
import Typography from "@mui/joy/Typography";
import { visuallyHidden } from "@mui/utils";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  Button,
  Flex,
  Heading,
  HoverCard,
  Popover,
  Text,
} from "@radix-ui/themes";
import { motion } from "framer-motion";
import Fuse from "fuse.js";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { currentUserId, userWeightUnit } from "../features/userSlice";
import { allBodyParts } from "../utils/exampleData";
import { supabase } from "../utils/supabase";

function createData(id, date, exerciseName, weight, reps, note) {
  return {
    id,
    date,
    exerciseName,
    weight,
    reps,
    note,
  };
}

function labelDisplayedRows({ from, to, count }) {
  return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
}

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }

  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "exerciseName",
    numeric: false,
    disablePadding: true,
    label: "Exercise Name",
  },
  {
    id: "weight",
    numeric: true,
    disablePadding: false,
    label: "Weight",
  },
  {
    id: "reps",
    numeric: true,
    disablePadding: false,
    label: "Reps",
  },
  {
    id: "date",
    numeric: false,
    disablePadding: false,
    label: "Date",
  },
  {
    id: "note",
    numeric: false,
    disablePadding: false,
    label: "Note",
  },
];

function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    currentLog,
    rowsPerPage,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const filteredHeadCells = headCells.filter(
    (headCell) =>
      currentLog === 0 ||
      (currentLog === 1 &&
        headCell.id !== "reps" &&
        headCell.id !== "exerciseName")
  );
  return (
    <thead>
      <tr>
        <th>
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowsPerPage}
            checked={numSelected === rowsPerPage}
            onChange={onSelectAllClick}
            slotProps={{
              input: {
                "aria-label": "select all displayed exercises",
              },
            }}
            sx={{ verticalAlign: "sub" }}
          />
        </th>
        {filteredHeadCells.map((headCell) => {
          const active = orderBy === headCell.id;
          return (
            <th
              key={headCell.id}
              aria-sort={
                active
                  ? { asc: "ascending", desc: "descending" }[order]
                  : undefined
              }
            >
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link
                underline="none"
                color="neutral"
                textColor={active ? "primary.plainColor" : undefined}
                component="button"
                onClick={createSortHandler(headCell.id)}
                fontWeight="lg"
                startDecorator={
                  headCell.numeric && currentLog !== 1 ? (
                    <ArrowDownwardIcon
                      sx={{
                        opacity: active ? 1 : 0,
                        display: {
                          xs: active ? "inherit" : "none",
                          lg: "inherit",
                        },
                      }}
                    />
                  ) : null
                }
                endDecorator={
                  !headCell.numeric || currentLog === 1 ? (
                    <ArrowDownwardIcon
                      sx={{
                        opacity: active ? 1 : 0,
                        display: {
                          xs: active ? "inherit" : "none",
                          lg: "inherit",
                        },
                      }}
                    />
                  ) : null
                }
                sx={{
                  "& svg": {
                    fontSize: { xs: 11, sm: 18, lg: 22 },
                    transition: "0.2s",
                    transform:
                      active && order === "desc"
                        ? "rotate(0deg)"
                        : "rotate(180deg)",
                  },
                  "&:hover": { "& svg": { opacity: 1 } },
                }}
              >
                {headCell.label}
                {active ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </Link>
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

function EnhancedTableToolbar(props) {
  const {
    numSelected,
    selected,
    setSelected,
    setCurrentDayData,
    isEditMode,
    setIsEditMode,
    currentDayData,
    selectedBodyPart,
    setSelectedBodyPart,
    handleExerciseSave,
    handleExerciseCancel,
    setEditedExerciseData,
    selectedDate,
    setSelectedDate,
    currentLog,
    isDesktop,
    searchValue,
    setSearchValue,
    setOrderBy,
    orderByRef,
  } = props;

  const userId = useSelector(currentUserId);
  const handleExerciseDelete = async () => {
    setCurrentDayData((prevData) =>
      prevData.filter((data) => !selected.includes(data.id))
    );
    enqueueSnackbar(`Exercise${numSelected > 1 ? "s" : ""} deleted!`, {
      variant: "info",
    });
    // Clear the selected array after deletion
    try {
      await supabase
        .from("exercise_data")
        .delete()
        // Use the `in` method to specify multiple values for deletion
        .in(
          "id",
          selected.map((data) => data)
        )
        .eq("user_id", userId);
    } catch (error) {
      console.error("Error deleting data:", error);
    }

    setSelected([]);
  };

  const handleExerciseEdit = () => {
    if (selected.length !== 1) {
      enqueueSnackbar("Please select exactly one exercise to edit", {
        variant: "error",
      });
      return;
    }

    const selectedIndex = currentDayData.findIndex(
      (data) => data.id === selected[0]
    );

    const selectedExercise = currentDayData.find(
      (data) => data.id === selected[0]
    );

    setEditedExerciseData({
      id: selectedExercise.id,
      exerciseName: selectedExercise.exerciseName,
      weight: selectedExercise.weight,
      reps: selectedExercise.reps,
      date: selectedExercise.date,
      bodypart: selectedExercise.bodypart,
      note: selectedExercise.note || "",
    });

    if (selectedIndex === -1) {
      enqueueSnackbar("Selected exercise not found", {
        variant: "error",
      });
      return;
    }

    if (!isEditMode) {
      // Apply the edited data to currentDayData
      setCurrentDayData((prevData) => {
        const newData = [...prevData];
        newData[selectedIndex] = {
          ...newData[selectedIndex],
        };
        return newData;
      });

      // Clear the edited data
    }

    setIsEditMode((prevEditMode) => !prevEditMode);
  };
  const handleSearchValueChange = (e) => {
    const inputValue = e.target.value;
    setSearchValue(inputValue);

    if (inputValue === "") {
      setOrderBy(orderByRef.current);
    } else {
      setOrderBy("");
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1,
        pl: { xs: 0.5, md: 1 },
        pr: 1,
        ...(numSelected > 0 && {
          bgcolor: "background.level1",
        }),
        borderTopLeftRadius: "var(--unstable_actionRadius)",
        borderTopRightRadius: "var(--unstable_actionRadius)",
      }}
    >
      {currentLog === 0 && (
        <Input
          name="search"
          onChange={(e) => handleSearchValueChange(e)}
          value={searchValue}
          size="sm"
          placeholder="Search..."
          startDecorator={<SearchIcon sx={{ color: "#5999ff" }} />}
          sx={{
            width: { xs: "70%", sm: "60%", md: "35%" },
            borderRadius: "20px",
            "--Input-focusedThickness": "2px",
            "--Input-focusedInset": "inset",
          }}
          endDecorator={
            searchValue ? (
              <IconButton
                sx={{
                  borderRadius: "20px",
                }}
                onClick={() => {
                  setSearchValue("");
                  setOrderBy(orderByRef.current);
                }}
              >
                <CloseSharpIcon sx={{ maxWidth: "20px", maxHeight: "20px" }} />
              </IconButton>
            ) : null
          }
        />
      )}
      <Box sx={{ width: "100%" }}>
        {numSelected > 0 ? (
          <Typography
            sx={{
              paddingRight: currentLog === 0 ? { sm: 15, lg: 25 } : 0,
            }}
            component="div"
          >
            {numSelected} selected
          </Typography>
        ) : (
          <Typography
            level={isDesktop ? "body-lg" : "body-md"}
            id="tableTitle"
            component="div"
            sx={{
              paddingRight: currentLog === 0 ? { sm: 25, lg: 35 } : 0,
            }}
          >
            {currentLog === 0 ? "Exercise Log" : "Bodyweight Log"}
          </Typography>
        )}
      </Box>
      {(selectedBodyPart || selectedDate) && (
        <Tooltip title="Clear Filters" sx={{ mr: 1 }}>
          <IconButton
            size="sm"
            color="neutral"
            variant="solid"
            onClick={() => {
              setSelectedBodyPart(null);
              setSelectedDate(null);
            }}
          >
            <FilterListOffIcon />
          </IconButton>
        </Tooltip>
      )}
      {numSelected > 0 ? (
        <>
          {isEditMode ? (
            <>
              <Tooltip title="Save">
                <IconButton
                  size="sm"
                  color="success"
                  variant="solid"
                  onClick={handleExerciseSave}
                >
                  <CheckCircleIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton
                  size="sm"
                  color="neutral"
                  variant="solid"
                  sx={{ ml: 1 }}
                  onClick={handleExerciseCancel}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Edit">
              <IconButton
                size="sm"
                color={isEditMode ? "success" : "primary"}
                variant="solid"
                onClick={handleExerciseEdit}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Delete" sx={{ ml: 1 }}>
            <IconButton
              size="sm"
              color="danger"
              variant="solid"
              onClick={handleExerciseDelete}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <Popover.Root>
          <Popover.Trigger>
            <Tooltip title="Filters">
              <IconButton size="sm" variant="outlined" color="neutral">
                <FilterListIcon />
              </IconButton>
            </Tooltip>
          </Popover.Trigger>

          <Popover.Content style={{ width: 250 }}>
            <Flex direction="column" gap="4" align="center">
              <Select
                placeholder="Filter by body part"
                disabled={currentLog === 1}
                defaultValue={selectedBodyPart}
                indicator={<KeyboardArrowDownIcon />}
                sx={{
                  textTransform: "capitalize",
                  [`& .${selectClasses.indicator}`]: {
                    transition: "0.2s",
                    [`&.${selectClasses.expanded}`]: {
                      transform: "rotate(-180deg)",
                    },
                  },
                  width: "100%",
                  height: "40px",
                }}
              >
                {allBodyParts.map((bodypart) => (
                  <Option
                    key={bodypart}
                    value={bodypart}
                    sx={{ textTransform: "capitalize" }}
                    onClick={() => setSelectedBodyPart(bodypart)}
                  >
                    {bodypart.charAt(0).toUpperCase() + bodypart.slice(1)}
                  </Option>
                ))}
              </Select>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ borderRadius: 20 }}
                  slotProps={{
                    textField: {
                      size: "small",
                    },
                    Field: {
                      borderRadius: "20px",
                    },
                  }}
                  label="Filter by date"
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue)}
                />
              </LocalizationProvider>
            </Flex>
          </Popover.Content>
        </Popover.Root>
      )}
    </Box>
  );
}

const TableSortAndSelection = ({ currentDayData, setCurrentDayData }) => {
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("date");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedExerciseData, setEditedExerciseData] = useState({
    exerciseName: "",
    weight: "",
    reps: "",
    date: "",
    bodypart: "",
    note: "",
  });
  const [selectedBodyPart, setSelectedBodyPart] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentLog, setCurrentLog] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const selectedExerciseData = useSelector((state) => state.exerciseData);
  const userId = useSelector(currentUserId);
  const checkDesktop = () => window.innerWidth > 768;
  const [isDesktop, setIsDesktop] = useState(checkDesktop());
  const orderByRef = useRef(orderBy);
  const textareaRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(checkDesktop());
    };

    window.addEventListener("resize", handleResize);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const delayInputTimeoutId = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 200);
    return () => clearTimeout(delayInputTimeoutId);
  }, [searchValue]);

  const handleEditInputChange = (field, value) => {
    if (field === "reps" || field === "weight") {
      const isNumeric = /^\d*\.?\d*$/.test(value);
      if (!isNumeric) {
        enqueueSnackbar("Only numbers are accepted for this field.", {
          variant: "error",
        });
        return;
      }
    }
    setEditedExerciseData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  useEffect(() => {
    localStorage.setItem("currentDayData", JSON.stringify(currentDayData));
  }, [currentDayData, selected, editedExerciseData]);

  useEffect(() => {
    setCurrentDayData(JSON.parse(localStorage.getItem("currentDayData")));
  }, [selectedExerciseData, selected]);

  useEffect(() => {
    if (selected.length > 1 && isEditMode) {
      setIsEditMode(false);
      enqueueSnackbar("You can only edit one exercise at a time", {
        variant: "error",
      });
      return;
    }
    setIsEditMode(false);
  }, [selected]);

  const fuseOptions = {
    includeScore: true,
    keys: ["exerciseName", "bodypart", "note", "weight", "reps"],
    threshold: 0.3,
    ignoreLocation: true,
  };
  const fuse = new Fuse(currentDayData, fuseOptions);
  const searchResults = fuse.search(debouncedSearch);
  const filteredData = currentDayData.filter((data) => {
    // Filter by body part if it's not "All"
    if (selectedBodyPart && data.bodypart !== selectedBodyPart) {
      return false;
    }

    if (
      searchResults.length > 0 &&
      !searchResults.some((result) => result.item.id === data.id)
    ) {
      return false;
    }

    if (
      (data.bodypart !== "bodyweight" && currentLog === 1) ||
      (data.bodypart === "bodyweight" && currentLog === 0)
    ) {
      return false;
    }
    // Filter by date if selectedDate is not "null"
    if (selectedDate !== null) {
      const dataDate = new Date(data.date);
      if (
        dataDate.getFullYear() !== selectedDate.$y ||
        dataDate.getMonth() !== selectedDate.$m ||
        dataDate.getDate() !== selectedDate.$D
      ) {
        return false;
      }
    }

    if (data.user_id !== userId) {
      return false;
    }

    return true;
  });

  const rows = filteredData.map((row) => {
    // Assuming row.date is a string representation of a date
    const dateObject = new Date(row.date);
    // Check if the dateObject is valid before calling toLocaleDateString
    if (!isNaN(dateObject.getTime())) {
      const note = (
        <Text>
          <HoverCard.Root>
            <HoverCard.Trigger>
              {row.note && (
                <Button
                  size="2"
                  variant="outline"
                  color="neutral"
                  style={{ padding: "0px 8px" }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Text>{isDesktop ? "Show Note" : "Note"}</Text>
                </Button>
              )}
            </HoverCard.Trigger>
            <HoverCard.Content
              style={{ maxWidth: "80vw", wordBreak: "break-word" }}
            >
              <Flex gap="4">
                <Box>
                  <Heading size="2" as="h3">
                    Note
                  </Heading>
                  <Text as="div" size="2" color="gray">
                    {row.note}
                  </Text>
                </Box>
              </Flex>
            </HoverCard.Content>
          </HoverCard.Root>
        </Text>
      );
      const filterColumn = currentLog === 1;
      return createData(
        row.id,
        dateObject,
        filterColumn ? null : row.exerciseName,
        row.weight,
        filterColumn ? null : row.reps,
        row.note ? note : ""
      );
    } else {
      // Handle the case where the date is invalid
      console.error(`Invalid date: ${row.date}`);
      return null;
    }
  });
  // Remove null entries if any

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    orderByRef.current = property;
  };

  // Select all the exercises on the current page

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = stableSort(rows, getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((item) => item.id);

      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    if (isEditMode && selected.length === 1 && selected[0] === id) {
      return; // Clicking on the selected row during edit mode, do nothing
    }

    let newSelected = [];
    const selectedIndex = selected.indexOf(id);

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((item) => item !== id);
    }

    setSelected(newSelected);
  };
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event, newValue) => {
    setRowsPerPage(parseInt(newValue.toString(), 10));
    setPage(0);
  };

  const getLabelDisplayedRowsTo = () => {
    if (rows.length === -1) {
      return (page + 1) * rowsPerPage;
    }
    return rowsPerPage === -1
      ? rows.length
      : Math.min(rows.length, (page + 1) * rowsPerPage);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const handleExerciseEdit = () => {
    if (selected.length === 1) {
      setIsEditMode(true);

      const selectedExercise = currentDayData.find(
        (exercise) => exercise.id === selected[0]
      );

      setEditedExerciseData({
        id: selectedExercise.id,
        exerciseName: selectedExercise.exerciseName,
        weight: selectedExercise.weight,
        reps: selectedExercise.reps,
        date: selectedExercise.date,
        bodypart: selectedExercise.bodypart,
        note: selectedExercise.note || "",
      });
    }
  };

  const handleExerciseSave = () => {
    try {
      setCurrentDayData((prevData) =>
        prevData.map((data) =>
          selected.includes(data.id)
            ? {
                ...data,
                exerciseName: editedExerciseData.exerciseName
                  .toLowerCase()
                  .trim(),
                weight: editedExerciseData.weight,
                reps: editedExerciseData.reps,
                bodypart: editedExerciseData.bodypart,
                date: editedExerciseData.date,
                note: editedExerciseData.note,
              }
            : data
        )
      );

      enqueueSnackbar("Exercise updated successfully!", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("An error occurred while updating the exercise.", {
        variant: "error",
      });
    }
    setSelected([]);
    setEditedExerciseData({
      exerciseName: "",
      weight: "",
      reps: "",
      bodypart: "",
      date: "",
      note: "",
    });
    setIsEditMode(false);
  };

  const handleExerciseCancel = () => {
    setIsEditMode(false);
    setSelected([]);
  };
  const weightUnitStore = useSelector(userWeightUnit);
  return (
    <div className="flex flex-col items-center xl:ml-72 mx-auto mt-6">
      <Tabs
        aria-label="tabs"
        defaultValue={0}
        sx={{ bgcolor: "transparent" }}
        onChange={(event, value) => setCurrentLog(value)}
        onClick={() => {
          if (isEditMode || selected > 0 || page > 0) setIsEditMode(false);
          setSelected([]);
          setPage(0);
        }}
      >
        <TabList
          disableUnderline
          sx={{
            p: 0,
            gap: 0.5,
            marginBottom: 1,
            borderRadius: "xl",
            bgcolor: "background.level1",
            [`& .${tabClasses.root}`]: {
              transition: "box-shadow 0.5s ease, background-color 0.5s ease",
            },
            [`& .${tabClasses.root}[aria-selected="true"]`]: {
              boxShadow: "sm",
              bgcolor: "background.surface",
            },
          }}
        >
          <Tab disableIndicator>Exercise Log</Tab>
          <Tab disableIndicator>Bodyweight Log</Tab>
        </TabList>
      </Tabs>
      <Box
        sx={{
          width: {
            xs: "100%",
            lg: "900px",
            xl: "1100px",
          },
          overflow: "auto",
          height: { xs: "360px", md: "400px", lg: "450px" },
          marginTop: 1,
        }}
      >
        <Sheet
          variant="outlined"
          sx={{
            boxShadow: "lg",
            borderRadius: "20px",
            mx: "auto",
            overflow: "auto",
            width: {
              xs: "100%",
              sm: currentLog === 0 ? "100%" : "80%",
              md: currentLog === 0 ? "80%" : "70%",
              lg: currentLog === 0 ? "900px" : "720px",
              xl: currentLog === 0 ? "1100px" : "900px",
            },

            transition: "width 0.5s ease",
          }}
        >
          <EnhancedTableToolbar
            numSelected={selected.length}
            currentDayData={currentDayData}
            setCurrentDayData={setCurrentDayData}
            selected={selected}
            setSelected={setSelected}
            setIsEditMode={setIsEditMode}
            isEditMode={isEditMode}
            handleExerciseSave={handleExerciseSave}
            handleExerciseCancel={handleExerciseCancel}
            handleExerciseEdit={handleExerciseEdit}
            setEditedExerciseData={setEditedExerciseData}
            selectedBodyPart={selectedBodyPart}
            setSelectedBodyPart={setSelectedBodyPart}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            setCurrentLog={setCurrentLog}
            currentLog={currentLog}
            isDesktop={isDesktop}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            setOrderBy={setOrderBy}
            orderByRef={orderByRef}
          />
          <Table
            aria-labelledby="tableTitle"
            hoverRow
            size={isDesktop ? "md" : "sm"}
            sx={{
              "--TableCell-headBackground": "transparent",
              "--TableCell-selectedBackground": (theme) =>
                theme.vars.palette.success.softBg,
              "& thead th:nth-of-type(1)": {
                width: { xs: "25px", sm: "35px", lg: "40px" },
              },
              "& thead th:nth-of-type(2)": {
                width: "30%",
                textAlign: "left",
              },
              "& tr > *:nth-of-type(n+3)": {
                textAlign: "right",
              },
              "& tr > td:nth-of-type(1)": {
                textAlign: currentLog === 0 ? "right" : "left",
              },
              textAlign: "right",
            }}
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
              setCurrentLog={setCurrentLog}
              currentLog={currentLog}
              isDesktop={isDesktop}
              rowsPerPage={rowsPerPage}
            />
            <tbody className="cursor-default">
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={currentLog === 0 ? 6 : 4}
                    style={{ textAlign: "center" }}
                  >
                    No data available
                  </td>
                </tr>
              ) : (
                stableSort(rows, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    const isItemSelected = isSelected(row.id);
                    const labelId = `enhanced-table-checkbox-${index}`;
                    const formattedDate = row.date.toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                    });
                    const weekDay = row.date.toLocaleDateString("en-IN", {
                      weekday: "short",
                    });

                    return (
                      <motion.tr
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 50 }}
                        transition={{ duration: 0.5, ease: "easeInOut" }}
                        onClick={(event) =>
                          handleClick(event, row.id, row.exerciseName)
                        }
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={row.id}
                        selected={isItemSelected}
                        style={
                          isItemSelected
                            ? {
                                "--TableCell-dataBackground":
                                  "var(--TableCell-selectedBackground)",
                                "--TableCell-headBackground":
                                  "var(--TableCell-selectedBackground)",
                                height: "45px",
                              }
                            : { height: "45px" }
                        }
                      >
                        <th scope="row">
                          <Checkbox
                            checked={isItemSelected}
                            slotProps={{
                              input: {
                                "aria-labelledby": labelId,
                              },
                            }}
                          />
                        </th>

                        {currentLog === 0 && (
                          <th
                            scope="row"
                            style={{
                              textTransform: "capitalize",
                            }}
                          >
                            <Tooltip
                              title={!isDesktop ? row.exerciseName : ""}
                              sx={{ textTransform: "capitalize" }}
                              variant="plain"
                            >
                              <div>
                                {isItemSelected && isEditMode ? (
                                  <Input
                                    onKeyDown={(e) =>
                                      e.key === "Enter" && handleExerciseSave()
                                    }
                                    value={editedExerciseData.exerciseName}
                                    onChange={(e) =>
                                      handleEditInputChange(
                                        "exerciseName",
                                        e.target.value
                                      )
                                    }
                                  />
                                ) : (
                                  row.exerciseName
                                )}
                              </div>
                            </Tooltip>
                          </th>
                        )}
                        <td>
                          {isItemSelected && isEditMode ? (
                            <Input
                              onKeyDown={(e) =>
                                e.key === "Enter" && handleExerciseSave()
                              }
                              value={editedExerciseData.weight}
                              onChange={(e) =>
                                handleEditInputChange("weight", e.target.value)
                              }
                            />
                          ) : (
                            row.weight +
                            (weightUnitStore === "imperial" ? "lbs" : "kg")
                          )}
                        </td>
                        {currentLog === 0 && (
                          <td>
                            {isItemSelected && isEditMode ? (
                              <Input
                                onKeyDown={(e) =>
                                  e.key === "Enter" && handleExerciseSave()
                                }
                                value={editedExerciseData.reps}
                                onChange={(e) =>
                                  handleEditInputChange("reps", e.target.value)
                                }
                              />
                            ) : (
                              row.reps
                            )}
                          </td>
                        )}
                        <Tooltip
                          title={row.date.toLocaleTimeString("en-IN", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                          variant="outlined"
                        >
                          <td>
                            {isItemSelected && isEditMode ? (
                              <Input
                                sx={{ paddingRight: { xs: 2.2 } }}
                                onKeyDown={(e) =>
                                  e.key === "Enter" && handleExerciseSave()
                                }
                                type="date"
                                value={
                                  new Date(editedExerciseData.date)
                                    .toISOString()
                                    .split("T")[0]
                                }
                                onChange={(e) =>
                                  handleEditInputChange("date", e.target.value)
                                }
                              />
                            ) : (
                              formattedDate + ` (${weekDay})`
                            )}
                          </td>
                        </Tooltip>
                        <td>
                          {isItemSelected && isEditMode ? (
                            <Popover.Root>
                              <Popover.Trigger>
                                <Button
                                  variant="solid"
                                  style={{
                                    backgroundColor: "#3e63dd",
                                    cursor: "pointer",
                                    width: "100%",
                                    gap: 2.5,
                                  }}
                                >
                                  {isDesktop && (
                                    <EditIcon
                                      sx={{
                                        fontSize: "17px",
                                      }}
                                    />
                                  )}
                                  {`Edit${isDesktop ? " Note" : ""}`}
                                </Button>
                              </Popover.Trigger>
                              <Popover.Content style={{ padding: 15 }}>
                                <Textarea
                                  onFocus={() =>
                                    textareaRef.current.setSelectionRange(
                                      textareaRef.current.value.length,
                                      textareaRef.current.value.length
                                    )
                                  }
                                  minRows={2}
                                  maxRows={4}
                                  sx={{ borderRadius: "12px" }}
                                  slotProps={{
                                    textarea: {
                                      placeholder: "Note",
                                      ref: textareaRef,
                                    },
                                  }}
                                  onKeyDown={(e) =>
                                    e.key === "Enter" && handleExerciseSave()
                                  }
                                  value={editedExerciseData.note}
                                  onChange={(e) =>
                                    handleEditInputChange(
                                      "note",
                                      e.target.value
                                    )
                                  }
                                />
                              </Popover.Content>
                            </Popover.Root>
                          ) : (
                            row.note
                          )}
                        </td>
                      </motion.tr>
                    );
                  })
              )}
              {emptyRows > 0 && (
                <tr
                  style={{
                    height: `calc(${emptyRows} * 45px)`,
                    "--TableRow-hoverBackground": "transparent",
                  }}
                >
                  <td colSpan={currentLog === 0 ? 6 : 4} aria-hidden />
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={currentLog === 0 ? 6 : 4}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      justifyContent: "flex-end",
                    }}
                  >
                    <FormControl orientation="horizontal" size="sm">
                      <FormLabel>Rows per page:</FormLabel>
                      <Select
                        onChange={handleChangeRowsPerPage}
                        value={rowsPerPage}
                      >
                        <Option value={5}>5</Option>
                        <Option value={10}>10</Option>
                        <Option value={25}>25</Option>
                      </Select>
                    </FormControl>
                    <Typography textAlign="center" sx={{ minWidth: 80 }}>
                      {labelDisplayedRows({
                        from: rows.length === 0 ? 0 : page * rowsPerPage + 1,
                        to: getLabelDisplayedRowsTo(),
                        count: rows.length === -1 ? -1 : rows.length,
                      })}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        size="sm"
                        color="neutral"
                        variant="outlined"
                        disabled={page === 0}
                        onClick={() => handleChangePage(page - 1)}
                        sx={{ bgcolor: "background.surface" }}
                      >
                        <KeyboardArrowLeftIcon />
                      </IconButton>
                      <IconButton
                        size="sm"
                        color="neutral"
                        variant="outlined"
                        disabled={
                          rows.length !== -1
                            ? page >= Math.ceil(rows.length / rowsPerPage) - 1
                            : false
                        }
                        onClick={() => handleChangePage(page + 1)}
                        sx={{ bgcolor: "background.surface" }}
                      >
                        <KeyboardArrowRightIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </td>
              </tr>
            </tfoot>
          </Table>
        </Sheet>
      </Box>
    </div>
  );
};

export default TableSortAndSelection;
